import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthWrapper';
import api from '../services/api';
import {
  Code2, LogOut, ChevronDown, Menu, LayoutGrid, BarChart3,
  Settings, Crown, Zap, MessageSquare, Cpu, Clock, TrendingUp,
  CheckCircle, Mail, ArrowRight, X, CreditCard, Building2, FileText,
  Download, Save, Receipt, Loader2, AlertTriangle
} from 'lucide-react';

const logo = '/elinnos-logo.png';

// ─── Helpers ───
const modelColors = {
  'claude-sonnet': { bg: 'bg-orange-500/10', text: 'text-orange-400', dot: 'bg-orange-400' },
  'claude-haiku': { bg: 'bg-amber-500/10', text: 'text-amber-400', dot: 'bg-amber-400' },
  'claude-opus': { bg: 'bg-red-500/10', text: 'text-red-400', dot: 'bg-red-400' },
  'gpt-4o': { bg: 'bg-green-500/10', text: 'text-green-400', dot: 'bg-green-400' },
  'gpt-4o-mini': { bg: 'bg-teal-500/10', text: 'text-teal-400', dot: 'bg-teal-400' },
  'elinnos': { bg: 'bg-blue-500/10', text: 'text-blue-400', dot: 'bg-blue-400' },
  'chipgen-1.1': { bg: 'bg-blue-500/10', text: 'text-blue-400', dot: 'bg-blue-400' },
  'gemini-2-flash': { bg: 'bg-purple-500/10', text: 'text-purple-400', dot: 'bg-purple-400' },
  'gemini-2-pro': { bg: 'bg-indigo-500/10', text: 'text-indigo-400', dot: 'bg-indigo-400' },
  'kimi-k3': { bg: 'bg-cyan-500/10', text: 'text-cyan-400', dot: 'bg-cyan-400' },
};
const getModelColor = (model) => modelColors[model] || { bg: 'bg-slate-500/10', text: 'text-slate-400', dot: 'bg-slate-400' };

// ─── Sidebar ───
const Sidebar = ({ activeSection, setActiveSection, user, onLogout, mobileOpen, setMobileOpen, onNavigate }) => {
  const navItems = [
    { id: 'overview', label: 'Overview', icon: LayoutGrid },
    { id: 'usage', label: 'Usage', icon: BarChart3 },
    { id: 'billing', label: 'Billing', icon: CreditCard },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      <aside className={`
        fixed top-0 left-0 h-screen w-64 z-50
        flex flex-col flex-shrink-0
        backdrop-blur-xl border-r border-cyan-500/10
        transition-transform duration-300
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `} style={{ background: 'linear-gradient(to right, rgba(10, 16, 32, 0.85) 0%, rgba(10, 16, 32, 0.4) 80%, transparent 100%)' }}>
        {/* Brand */}
        <div className="flex items-center gap-2.5 px-5 h-14 sm:h-16 border-b border-cyan-500/10 flex-shrink-0">
          <img src={logo} alt="Elinnos" className="h-11 w-auto" />
          <span className="text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">chipdesigner.ai</span>
          <button className="ml-auto lg:hidden text-slate-400 hover:text-white" onClick={() => setMobileOpen(false)} style={{ border: 'none', background: 'none' }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User card */}
        <div className="px-4 py-4 border-b border-cyan-500/10 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-cyan-400 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white truncate">{user?.name || 'User'}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email || ''}</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map(item => {
            const Icon = item.icon;
            const active = activeSection === item.id;
            return (
              <button
                key={item.id}
                onClick={() => { setActiveSection(item.id); setMobileOpen(false); }}
                className={`
                  w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer
                  ${active
                    ? 'bg-gradient-to-r from-blue-600/20 to-cyan-500/10 text-cyan-300 border border-cyan-500/20'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/50 border border-transparent'
                  }
                `}
                style={{ border: 'none', font: 'inherit' }}
              >
                <Icon className={`w-4 h-4 ${active ? 'text-cyan-400' : ''}`} />
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* Plan badge */}
        <div className="px-3 pb-3 flex-shrink-0">
          <div className="rounded-xl p-3.5 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(37,99,235,0.15) 0%, rgba(124,58,237,0.1) 100%)' }}>
            <div className="flex items-center gap-2 mb-1">
              <Crown className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-xs font-bold text-white">{user?.plan || 'Pro'}</span>
            </div>
            <p className="text-xs text-slate-400">Full access</p>
          </div>
        </div>

        {/* Logout */}
        <div className="px-3 pb-4 flex-shrink-0">
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10 transition-all cursor-pointer"
            style={{ border: 'none', font: 'inherit' }}
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>
    </>
  );
};

// ─── Stat Card ───
const StatCard = ({ icon: Icon, label, value, sublabel, color }) => (
  <div className="feature-ref-card group">
    <div className={`feature-icon-box ${color.bg} ${color.border} ${color.text} group-hover:shadow-[0_0_20px_${color.glow}] transition-all`}>
      <Icon className="w-6 h-6" />
    </div>
    <h3 className="text-3xl font-bold text-white mb-1">{value}</h3>
    <p className="text-sm text-slate-400">{label}</p>
    {sublabel && <p className="text-xs text-slate-500 mt-0.5">{sublabel}</p>}
  </div>
);

// ─── Usage Chart (CSS bars) ───
const UsageChart = ({ daily }) => {
  if (!daily || daily.length === 0) {
    return <div className="text-center py-12 text-slate-500 text-sm">No usage data yet</div>;
  }

  const maxCredits = Math.max(...daily.map(d => d.credits ?? 0), 1);

  return (
    <div className="mt-4">
      <div className="flex items-stretch justify-between gap-1" style={{ height: 200 }}>
        {daily.map((d, i) => {
          const heightPct = ((d.credits ?? 0) / maxCredits) * 100;
          const dayLabel = new Date(d.date).toLocaleDateString('en', { weekday: 'short' });
          return (
            <div key={i} className="flex-1 flex flex-col items-center justify-end group relative h-full">
              {/* Tooltip */}
              <div className="absolute -top-10 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-800 rounded-lg px-2 py-1 text-xs text-white whitespace-nowrap z-10 pointer-events-none">
                {(d.credits ?? 0).toFixed(1)} cre · {d.requests} req
              </div>
              {/* Bar */}
              <div
                className="w-full max-w-[40px] rounded-t-md transition-all duration-300 group-hover:opacity-80"
                style={{
                  height: `${Math.max(heightPct, 2)}%`,
                  minHeight: '4px',
                  background: (d.credits ?? 0) > 0
                    ? 'linear-gradient(to top, rgba(59,130,246,0.6), rgba(6,182,212,0.8))'
                    : 'rgba(30,41,59,0.4)',
                }}
              />
              {/* Label */}
              <span className="text-xs text-slate-500 mt-2">{dayLabel}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Model Breakdown ───
const ModelBreakdown = ({ models }) => {
  if (!models || models.length === 0) {
    return <div className="text-center py-8 text-slate-500 text-sm">No model usage data yet</div>;
  }

  return (
    <div className="space-y-3">
      {models.map((m, i) => {
        const color = getModelColor(m.model);
        return (
          <div key={i} className="flex items-center gap-3">
            <div className={`w-2.5 h-2.5 rounded-full ${color.dot} flex-shrink-0`} />
            <span className="text-sm text-slate-300 w-32 truncate">{m.model}</span>
            <div className="flex-1 h-2 rounded-full bg-slate-800/60 overflow-hidden">
              <div
                className={`h-full rounded-full ${color.dot} opacity-70 transition-all duration-500`}
                style={{ width: `${m.percentage}%` }}
              />
            </div>
            <span className="text-xs text-slate-400 w-12 text-right">{m.percentage}%</span>
            <span className="text-xs text-slate-500 w-16 text-right">{(m.credits ?? 0).toFixed(1)} cre</span>
          </div>
        );
      })}
    </div>
  );
};

// ─── Loading Skeleton ───
const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse bg-slate-800/40 rounded-lg ${className}`} />
);

// ─── Main Dashboard Component ───
const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Data state
  const [stats, setStats] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  // Billing state
  const [billingStatus, setBillingStatus] = useState(null);
  const [billingProfile, setBillingProfile] = useState(null);
  const [billingInvoices, setBillingInvoices] = useState([]);
  const [billingPayments, setBillingPayments] = useState([]);
  const [billingForm, setBillingForm] = useState({
    billing_company: '', billing_gstin: '', billing_address_line1: '',
    billing_address_line2: '', billing_city: '', billing_state: '',
    billing_pincode: '', billing_country: 'India', billing_phone: '',
  });
  const [savingBilling, setSavingBilling] = useState(false);
  const [billingSaved, setBillingSaved] = useState(false);

  const getBaseUrl = () => process.env.REACT_APP_API_BASE_URL || '';
  const getToken = () => {
    const cookies = document.cookie.split(';').map(c => c.trim());
    const sc = cookies.find(c => c.startsWith('session_token='));
    return sc ? sc.split('=')[1] : '';
  };

  const fetchBillingData = useCallback(async () => {
    try {
      const token = getToken();
      const headers = { 'Authorization': `Bearer ${token}` };
      const [sRes, pRes, iRes] = await Promise.all([
        fetch(`${getBaseUrl()}/api/billing/status`, { headers }).catch(() => null),
        fetch(`${getBaseUrl()}/api/billing/profile`, { headers }).catch(() => null),
        fetch(`${getBaseUrl()}/api/billing/invoices`, { headers }).catch(() => null),
      ]);
      if (sRes?.ok) setBillingStatus(await sRes.json());
      if (pRes?.ok) {
        const p = await pRes.json();
        setBillingProfile(p);
        setBillingForm({
          billing_company: p.billing_company || '', billing_gstin: p.billing_gstin || '',
          billing_address_line1: p.billing_address_line1 || '', billing_address_line2: p.billing_address_line2 || '',
          billing_city: p.billing_city || '', billing_state: p.billing_state || '',
          billing_pincode: p.billing_pincode || '', billing_country: p.billing_country || 'India',
          billing_phone: p.billing_phone || '',
        });
      }
      if (iRes?.ok) {
        const d = await iRes.json();
        setBillingInvoices(d.invoices || []);
        setBillingPayments(d.payments || []);
      }
    } catch (err) {}
  }, []);

  const handleSaveBillingProfile = async () => {
    setSavingBilling(true);
    try {
      const resp = await fetch(`${getBaseUrl()}/api/billing/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify(billingForm),
      });
      if (resp.ok) {
        setBillingSaved(true);
        setTimeout(() => setBillingSaved(false), 3000);
      }
    } catch (err) {}
    setSavingBilling(false);
  };

  const handleCreateInvoice = async (paymentId) => {
    try {
      const resp = await fetch(`${getBaseUrl()}/api/billing/invoice/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ payment_id: paymentId }),
      });
      const data = await resp.json();
      if (resp.ok && data.short_url) window.open(data.short_url, '_blank');
    } catch (err) {}
  };

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const [statsRes, usageRes] = await Promise.all([
        api.getDashboardStats().catch(() => null),
        api.getDashboardUsage(7).catch(() => null),
      ]);
      if (statsRes) setStats(statsRes);
      if (usageRes) setUsage(usageRes);
      if (!statsRes && !usageRes) setFetchError(true);
    } catch (err) {
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchDashboardData();
    fetchBillingData();

    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [fetchDashboardData]);

  // Fetch billing data when billing section is opened
  useEffect(() => {
    if (activeSection === 'billing' && !billingStatus) {
      fetchBillingData();
    }
  }, [activeSection, billingStatus, fetchBillingData]);

  if (!user) {
    navigate('/');
    return null;
  }

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navigateToLandingSection = (sectionId) => {
    navigate(`/?section=${sectionId}`);
  };

  return (
    <div className="hero-ref-bg text-slate-100 min-h-screen relative page-transition flex">

      {/* Sidebar */}
      <Sidebar
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        user={user}
        onLogout={handleLogout}
        mobileOpen={mobileSidebarOpen}
        setMobileOpen={setMobileSidebarOpen}
        onNavigate={navigate}
      />

      {/* Main content area */}
      <div className="flex-1 min-w-0 flex flex-col h-screen overflow-y-auto lg:ml-64">

        {/* Top header */}
        <header className="sticky top-0 z-30 header-ref-nav">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-14 sm:h-16">
              {/* Mobile menu button */}
              <button
                className="lg:hidden text-slate-300 hover:text-white"
                onClick={() => setMobileSidebarOpen(true)}
                style={{ border: 'none', background: 'none' }}
              >
                <Menu className="w-5 h-5" />
              </button>

              {/* Desktop: section title */}
              <div className="hidden lg:flex items-center gap-2">
                <span className="text-sm font-semibold text-white capitalize">{activeSection}</span>
              </div>

              {/* Right: nav links + user menu */}
              <div className="flex items-center gap-4">
                <div className="hidden md:flex items-center gap-6">
                  <button onClick={() => navigateToLandingSection('features')} className="nav-tab" style={{ border: 'none', background: 'none', font: 'inherit' }}>Features</button>
                  <button onClick={() => navigateToLandingSection('demo')} className="nav-tab" style={{ border: 'none', background: 'none', font: 'inherit' }}>Platform</button>
                  <button onClick={() => navigate('/dashboard')} className="nav-tab" style={{ border: 'none', background: 'none', font: 'inherit' }}>Dashboard</button>
                  <button onClick={() => navigate('/pricing')} className="nav-tab" style={{ border: 'none', background: 'none', font: 'inherit' }}>Pricing</button>
                  <button onClick={() => navigate('/billing')} className="nav-tab" style={{ border: 'none', background: 'none', font: 'inherit' }}>Billing</button>
                </div>

                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2.5 px-3 py-1.5 rounded-full bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700/60 transition-all cursor-pointer"
                    style={{ border: '1px solid rgba(51,65,85,0.6)' }}
                  >
                    <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-blue-600 to-cyan-400 flex items-center justify-center text-white font-bold text-xs">
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-slate-900/95 backdrop-blur-xl rounded-xl shadow-2xl border border-slate-700/60 py-2 z-50">
                      <div className="px-4 py-2 border-b border-slate-800">
                        <p className="text-sm font-semibold text-white">{user?.name || 'User'}</p>
                        <p className="text-xs text-slate-400 truncate">{user?.email || ''}</p>
                      </div>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer text-left"
                        style={{ background: 'none', border: 'none', font: 'inherit' }}
                      >
                        <LogOut className="w-4 h-4" />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 px-4 sm:px-6 lg:px-8 pt-6 pb-12 max-w-6xl mx-auto w-full">

          {/* ─── Trial / subscription expiry banner ─── */}
          {billingStatus?.subscription_ended && (
            <div role="alert" className="mb-6 flex items-center justify-between gap-4 rounded-xl border border-red-500/40 bg-red-950/40 px-5 py-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-red-300">Subscription ended</p>
                  <p className="text-xs text-red-400/80">Your paid plan has expired — AI features are disabled.</p>
                </div>
              </div>
              <button onClick={() => navigate('/pricing')}
                className="shrink-0 px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-sm font-medium rounded-lg cursor-pointer" style={{ border: 'none' }}>
                Subscribe to Pro
              </button>
            </div>
          )}
          {!billingStatus?.subscription_ended && billingStatus?.trial_expired && (
            <div role="alert" className="mb-6 flex items-center justify-between gap-4 rounded-xl border border-red-500/40 bg-red-950/40 px-5 py-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-red-300">Free trial expired</p>
                  <p className="text-xs text-red-400/80">Your 10-day trial is over — AI features are disabled.</p>
                </div>
              </div>
              <button onClick={() => navigate('/pricing')}
                className="shrink-0 px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-sm font-medium rounded-lg cursor-pointer" style={{ border: 'none' }}>
                Subscribe to Pro
              </button>
            </div>
          )}
          {!billingStatus?.subscription_ended && !billingStatus?.trial_expired && billingStatus?.trial_ends_at && billingStatus?.plan === 'free' && (
            <div className="mb-6 flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-950/30 px-5 py-3">
              <Clock className="w-4 h-4 text-amber-400 shrink-0" />
              <p className="text-xs text-amber-300">
                Free trial ends {new Date(billingStatus.trial_ends_at).toLocaleDateString()} —{' '}
                <button onClick={() => navigate('/pricing')} className="underline font-semibold cursor-pointer" style={{ background: 'none', border: 'none', color: 'inherit' }}>
                  upgrade to Pro
                </button>{' '}to keep access.
              </p>
            </div>
          )}

          {/* ─── Overview Section ─── */}
          {activeSection === 'overview' && (
            <div className="space-y-8">
              {/* Welcome */}
              <div className="dl-fade-up">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4 bg-cyan-950/60 backdrop-blur-md border border-cyan-500/30">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-cyan-300">Dashboard</span>
                </div>
                <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
                  Welcome back, <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">{user?.name || 'User'}</span>
                </h1>
                <p className="text-slate-400 max-w-2xl">
                  Track your AI usage and manage your ChipDesigner.ai account
                </p>
              </div>

              {/* Stat cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {loading ? (
                  <>
                    <Skeleton className="h-32" />
                    <Skeleton className="h-32" />
                    <Skeleton className="h-32" />
                  </>
                ) : (
                  <>
                    <StatCard
                      icon={Zap}
                      label="Total Requests"
                      value={stats?.total_requests ?? 0}
                      sublabel="All-time AI calls"
                      color={{ bg: 'bg-blue-500/10', border: 'border-blue-400/20', text: 'text-blue-400', glow: 'rgba(59,130,246,0.4)' }}
                    />
                    <StatCard
                      icon={Cpu}
                      label="Credits Used"
                      value={(stats?.total_credits ?? 0).toFixed(1)}
                      sublabel="All-time"
                      color={{ bg: 'bg-purple-500/10', border: 'border-purple-400/20', text: 'text-purple-400', glow: 'rgba(168,85,247,0.4)' }}
                    />
                    <StatCard
                      icon={MessageSquare}
                      label="Active Models"
                      value={stats?.active_models ?? 0}
                      sublabel="Distinct AI models used"
                      color={{ bg: 'bg-cyan-500/10', border: 'border-cyan-400/20', text: 'text-cyan-400', glow: 'rgba(6,182,212,0.4)' }}
                    />
                  </>
                )}
              </div>

              {/* Credit quota usage */}
              {!loading && stats?.quota?.daily && (
                <div className="feature-ref-card p-6">
                  <h3 className="text-lg font-bold text-white mb-4">AI Credit Quota</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[
                      { label: 'Today', d: stats.quota.daily },
                      { label: 'This week', d: stats.quota.weekly },
                    ].map(({ label, d }) => {
                      const limit = d?.limit;
                      const used = d?.used ?? 0;
                      const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : null;
                      return (
                        <div key={label}>
                          <div className="flex justify-between text-sm mb-1.5">
                            <span className="text-slate-400">{label}</span>
                            <span className={pct !== null && pct >= 80 ? 'text-amber-400' : 'text-slate-200'}>
                              {limit === -1
                                ? <>{used.toFixed(1)} cre · <span className="text-slate-500">unlimited</span></>
                                : <>{used.toFixed(1)} / {limit} cre{pct !== null && <span className="text-slate-500"> ({pct}%)</span>}</>}
                            </span>
                          </div>
                          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${pct !== null && pct >= 100 ? 'bg-red-400' : pct !== null && pct >= 80 ? 'bg-amber-400' : 'bg-cyan-400'}`}
                              style={{ width: `${pct ?? 2}%` }}
                            />
                          </div>
                          {d?.reset_at && (
                            <p className="text-[11px] text-slate-500 mt-1.5">
                              resets {new Date(d.reset_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Usage chart + Model breakdown */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="feature-ref-card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-white">Usage (Last 7 Days)</h3>
                    <TrendingUp className="w-5 h-5 text-slate-500" />
                  </div>
                  {loading ? <Skeleton className="h-40" /> : <UsageChart daily={usage?.daily} />}
                </div>

                <div className="feature-ref-card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-white">Model Breakdown</h3>
                    <Cpu className="w-5 h-5 text-slate-500" />
                  </div>
                  {loading ? <Skeleton className="h-40" /> : <ModelBreakdown models={usage?.model_breakdown} />}
                </div>
              </div>

            </div>
          )}

          {/* ─── Usage Section ─── */}
          {activeSection === 'usage' && (
            <div className="space-y-8">
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">Usage Analytics</h1>
                <p className="text-slate-400">Detailed breakdown of your AI credit usage</p>
              </div>

              {fetchError && (
                <div className="feature-ref-card p-6 text-center">
                  <p className="text-slate-400 text-sm mb-4">Unable to load usage data. Your session may have expired.</p>
                  <button
                    onClick={() => fetchDashboardData()}
                    className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors cursor-pointer"
                    style={{ border: 'none' }}
                  >
                    Retry
                  </button>
                </div>
              )}

              {!fetchError && (
                <>
                  <div className="feature-ref-card p-6">
                    <h3 className="text-lg font-bold text-white mb-4">Daily Credit Usage</h3>
                    {loading ? <Skeleton className="h-40" /> : <UsageChart daily={usage?.daily} />}
                  </div>

                  <div className="feature-ref-card p-6">
                    <h3 className="text-lg font-bold text-white mb-4">Model Distribution</h3>
                    {loading ? <Skeleton className="h-40" /> : <ModelBreakdown models={usage?.model_breakdown} />}
                  </div>

                  {/* Stats grid */}
                  {!loading && stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="feature-ref-card p-5 text-center">
                        <Zap className="w-5 h-5 text-blue-400 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-white">{stats.total_requests}</p>
                        <p className="text-xs text-slate-400">Total Requests</p>
                      </div>
                      <div className="feature-ref-card p-5 text-center">
                        <Cpu className="w-5 h-5 text-purple-400 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-white">{(stats.total_credits ?? 0).toFixed(1)}</p>
                        <p className="text-xs text-slate-400">Total Credits</p>
                      </div>
                      <div className="feature-ref-card p-5 text-center">
                        <MessageSquare className="w-5 h-5 text-cyan-400 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-white">{stats.active_models}</p>
                        <p className="text-xs text-slate-400">Active Models</p>
                      </div>
                      <div className="feature-ref-card p-5 text-center">
                        <Crown className="w-5 h-5 text-amber-400 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-white capitalize">{stats.plan || 'Pro'}</p>
                        <p className="text-xs text-slate-400">Current Plan</p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ─── Billing Section ─── */}
          {activeSection === 'billing' && (
            <div className="space-y-6">
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">Billing</h1>
                <p className="text-slate-400">Manage your subscription, billing profile, and invoices</p>
              </div>

              {/* Current Plan */}
              <div className="feature-ref-card p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-blue-400" />
                  Current Plan
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-slate-700/50">
                    <span className="text-slate-400 text-sm">Plan</span>
                    <span className="font-semibold text-white text-sm capitalize">{billingStatus?.plan || user?.plan || 'free'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-700/50">
                    <span className="text-slate-400 text-sm">Status</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      billingStatus?.subscription_status === 'active' ? 'bg-green-500/20 text-green-400' :
                      billingStatus?.subscription_status === 'canceled' ? 'bg-red-500/20 text-red-400' :
                      'bg-slate-700 text-slate-400'
                    }`}>
                      {billingStatus?.subscription_status || 'none'}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-700/50">
                    <span className="text-slate-400 text-sm">Billing Type</span>
                    <span className="font-semibold text-white text-sm">
                      {billingStatus?.billing_type === 'subscription' ? 'Auto-pay monthly' :
                       billingStatus?.billing_type === 'one_time' ? 'One-time payment' : 'None'}
                    </span>
                  </div>
                  {billingStatus?.renews_at && (
                    <div className="flex justify-between py-2 border-b border-slate-700/50">
                      <span className="text-slate-400 text-sm">Renews At</span>
                      <span className="font-semibold text-white text-sm">{new Date(billingStatus.renews_at).toLocaleDateString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-2">
                    <span className="text-slate-400 text-sm">
                      {billingStatus?.plan === 'free' ? 'Trial Ends' : 'AI Access'}
                    </span>
                    <span className="font-semibold text-white text-sm">
                      {billingStatus?.trial_ends_at && billingStatus?.plan === 'free'
                        ? new Date(billingStatus.trial_ends_at).toLocaleDateString()
                        : billingStatus?.plan === 'free' ? 'Trial' : 'Unlimited (credit quota applies)'}
                    </span>
                  </div>
                </div>
                {(!billingStatus?.plan || billingStatus?.plan === 'free') && (
                  <button
                    onClick={() => navigate('/pricing')}
                    className="w-full mt-4 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white rounded-xl font-medium transition-all flex items-center justify-center gap-2 cursor-pointer"
                    style={{ border: 'none' }}
                  >
                    Upgrade Plan <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Billing Profile (GST) */}
              <div className="feature-ref-card p-6">
                <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-400" />
                  Billing Profile (GST & Address)
                </h3>
                <p className="text-slate-400 text-sm mb-4">This information appears on your GST invoices.</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <BillingInput label="Company Name" value={billingForm.billing_company} onChange={v => setBillingForm({...billingForm, billing_company: v})} placeholder="Acme Corp Pvt Ltd" />
                  <BillingInput label="GSTIN" value={billingForm.billing_gstin} onChange={v => setBillingForm({...billingForm, billing_gstin: v.toUpperCase()})} placeholder="29ABCDE1234F1Z5" maxLength={15} />
                  <div className="md:col-span-2">
                    <BillingInput label="Address Line 1" value={billingForm.billing_address_line1} onChange={v => setBillingForm({...billingForm, billing_address_line1: v})} placeholder="123, Industrial Area" />
                  </div>
                  <div className="md:col-span-2">
                    <BillingInput label="Address Line 2" value={billingForm.billing_address_line2} onChange={v => setBillingForm({...billingForm, billing_address_line2: v})} placeholder="Building, Floor (optional)" />
                  </div>
                  <BillingInput label="City" value={billingForm.billing_city} onChange={v => setBillingForm({...billingForm, billing_city: v})} placeholder="Bengaluru" />
                  <BillingInput label="State" value={billingForm.billing_state} onChange={v => setBillingForm({...billingForm, billing_state: v})} placeholder="Karnataka" />
                  <BillingInput label="Pincode" value={billingForm.billing_pincode} onChange={v => setBillingForm({...billingForm, billing_pincode: v.replace(/\D/g, '')})} placeholder="560001" maxLength={6} />
                  <BillingInput label="Phone" value={billingForm.billing_phone} onChange={v => setBillingForm({...billingForm, billing_phone: v})} placeholder="+91 98765 43210" />
                </div>
                <div className="flex items-center gap-3 mt-4">
                  <button
                    onClick={handleSaveBillingProfile}
                    disabled={savingBilling}
                    className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-medium transition-colors flex items-center gap-2 cursor-pointer"
                    style={{ border: 'none' }}
                  >
                    {savingBilling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {savingBilling ? 'Saving...' : 'Save Profile'}
                  </button>
                  {billingSaved && (
                    <span className="text-green-400 text-sm flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> Saved!
                    </span>
                  )}
                </div>
              </div>

              {/* Payment History */}
              <div className="feature-ref-card p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-blue-400" />
                  Payment History & Invoices
                </h3>
                {billingInvoices.length === 0 && billingPayments.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No payments yet. Your invoices will appear here after payment.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {billingInvoices.map(inv => (
                      <div key={inv.invoice_id} className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-slate-400" />
                          <div>
                            <p className="text-sm font-medium text-white">Invoice #{inv.invoice_number || inv.invoice_id?.slice(-8)}</p>
                            <p className="text-xs text-slate-500">
                              {inv.created_at ? new Date(inv.created_at * 1000).toLocaleDateString('en-IN') : ''} ·
                              ₹{(inv.amount / 100).toFixed(2)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${inv.paid ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}`}>
                            {inv.status}
                          </span>
                          {inv.short_url && (
                            <a href={inv.short_url} target="_blank" rel="noopener noreferrer" className="p-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded-lg transition-colors">
                              <Download className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                    {billingPayments.filter(p => !p.invoice_id).map(pay => (
                      <div key={pay.payment_id} className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
                        <div className="flex items-center gap-3">
                          <CreditCard className="w-5 h-5 text-slate-400" />
                          <div>
                            <p className="text-sm font-medium text-white">₹{(pay.amount / 100).toFixed(2)}</p>
                            <p className="text-xs text-slate-500">
                              {pay.created_at ? new Date(pay.created_at * 1000).toLocaleDateString('en-IN') : ''} · {pay.method || 'card'}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleCreateInvoice(pay.payment_id)}
                          className="px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 cursor-pointer"
                          style={{ border: 'none' }}
                        >
                          <FileText className="w-4 h-4" /> Get Invoice
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

        </main>

        {/* Footer — same as landing page */}
        <footer className="border-t border-cyan-500/10 text-white py-6 relative z-10 mt-auto" style={{ background: 'linear-gradient(to top, rgba(10, 16, 32, 0.8) 0%, rgba(10, 16, 32, 0.3) 70%, transparent 100%)' }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <img src={logo} alt="Elinnos" className="h-6 w-auto" />
                <span className="text-sm font-bold gradient-text">chipdesigner.ai</span>
              </div>

              <div className="flex items-center gap-6">
                <a href="/#features" className="text-sm text-slate-400 hover:text-white transition-colors">Features</a>
                <a href="/#demo" className="text-sm text-slate-400 hover:text-white transition-colors">Demo</a>
                <a href="https://elinnos.com" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 hover:text-white transition-colors">Elinnos</a>
                <a href="mailto:contact@chipdesigner.ai" className="text-sm text-slate-400 hover:text-white transition-colors">Contact</a>
              </div>
            </div>

            <div className="border-t border-cyan-500/10 mt-4 pt-3 text-center">
              <p className="text-xs text-slate-500">&copy; 2026 chipdesigner.ai. All rights reserved.</p>
            </div>
          </div>
        </footer>

      </div>
    </div>
  );
};

export default Dashboard;

const BillingInput = ({ label, value, onChange, placeholder, maxLength }) => (
  <div>
    <label className="text-xs text-slate-400 mb-1 block">{label}</label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white text-sm placeholder-slate-600 focus:outline-none focus:border-blue-500 transition-colors"
    />
  </div>
);
